/**
 * Lightweight global signal that "something push-related is happening right now"
 * (subscribing, re-subscribing, awaiting a test receipt, or the user is sitting
 * on the notifications card). The PWA version checker / forced-refresh flow
 * reads this on iOS standalone and defers reloading so it doesn't tear down the
 * service worker mid-handshake.
 *
 * Intentionally framework-free so it can be imported from non-React modules
 * (version-check.ts, app-refresh.ts) without circular deps.
 */

const ACTIVITY_KEY = "__teamvote_push_activity_count__";
const SUSPEND_UNTIL_KEY = "__teamvote_push_suspend_until__";

function getCount(): number {
  return (window as any)[ACTIVITY_KEY] ?? 0;
}

function setCount(n: number): void {
  (window as any)[ACTIVITY_KEY] = Math.max(0, n);
}

/**
 * Mark a push-sensitive operation as in flight. Returns a release function.
 * Callers MUST call release() (use try/finally).
 */
export function beginPushActivity(): () => void {
  if (typeof window === "undefined") return () => {};
  setCount(getCount() + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    setCount(getCount() - 1);
  };
}

/**
 * Suspend forced refreshes for the next `ms` milliseconds. Useful when the
 * user opens the notifications card — even idle time on that screen should
 * not be interrupted by an auto-reload.
 */
export function suspendRefreshes(ms: number): void {
  if (typeof window === "undefined") return;
  const until = Date.now() + ms;
  const current: number = (window as any)[SUSPEND_UNTIL_KEY] ?? 0;
  if (until > current) (window as any)[SUSPEND_UNTIL_KEY] = until;
}

/**
 * Should the PWA refresh/version-check pipeline pause right now?
 * True if any activity is in flight OR a temporary suspension window is open.
 */
export function isPushActivitySuspended(): boolean {
  if (typeof window === "undefined") return false;
  if (getCount() > 0) return true;
  const until: number = (window as any)[SUSPEND_UNTIL_KEY] ?? 0;
  return Date.now() < until;
}
